const { formatMoney } = window.BAO.Utils

export class ProductQuickAddPost extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'change', this.onChangeHandler.bind(this))

    const checkedPost = this.querySelector(
      '[data-quick-add-post-el="post-radio"]:checked',
    )
    if (checkedPost) this.onPostSelected(checkedPost)

    this.updateSubmitButton()
  }

  /**
   * @param {Event} event
   */
  onChangeHandler (event) {
    const { target } = event

    if (target.matches('[data-quick-add-post-el="post-radio"]')) {
      this.onPostSelected(target)
    } else if (target.matches('[data-quick-add-post-el="size-select"]')) {
      this.onSizeSelected(target)
    }
  }

  /**
   * @param {HTMLInputElement} radio
   */
  onPostSelected (radio) {
    const postProductId = radio.value
    const sizeRow = this.querySelector('[data-quick-add-post-el="size-row"]')
    const sizeSelect = this.querySelector(
      '[data-quick-add-post-el="size-select"]',
    )
    const sizesScript = this.querySelector(
      `[data-quick-add-post-sizes="${postProductId}"]`,
    )

    if (!sizeRow || !sizeSelect || !sizesScript) return

    const postLabelValue = this.querySelector(
      '[data-quick-add-post-el="post-label-value"]',
    )
    if (postLabelValue) {
      postLabelValue.textContent =
        radio.nextElementSibling
          ?.querySelector('.prd-QuickAdd_VariantTitle')
          ?.textContent.trim() ?? ''
    }

    const sizes = JSON.parse(sizesScript.textContent)

    const placeholder = sizeSelect.dataset.placeholder || ''
    sizeSelect.innerHTML = `<option value="">${placeholder}</option>`
    sizes.forEach(({ id, title, available, price }) => {
      const option = document.createElement('option')
      option.value = id
      option.textContent = title
      option.disabled = !available
      if (price != null) option.dataset.price = price
      sizeSelect.appendChild(option)
    })

    const variantInput = this.variantInput
    if (variantInput) variantInput.value = ''

    const productInput = this.productInput
    if (productInput) productInput.value = ''

    // Changing the post clears the size selection, so reset its label value too.
    const sizeLabelValue = this.querySelector(
      '[data-quick-add-post-el="size-label-value"]',
    )
    if (sizeLabelValue) sizeLabelValue.textContent = ''

    sizeRow.hidden = false

    this.updatePrice()
    this.updateSubmitButton()
  }

  /**
   * @param {HTMLSelectElement} select
   */
  onSizeSelected (select) {
    const variantInput = this.variantInput
    if (variantInput) variantInput.value = select.value

    // VennApps needs the post line's product id; mirror it to the variant.
    const productInput = this.productInput
    if (productInput) {
      const checkedPost = this.querySelector(
        '[data-quick-add-post-el="post-radio"]:checked',
      )
      productInput.value = select.value && checkedPost ? checkedPost.value : ''
    }

    const sizeLabelValue = this.querySelector(
      '[data-quick-add-post-el="size-label-value"]',
    )
    if (sizeLabelValue) {
      sizeLabelValue.textContent = select.value
        ? select.selectedOptions[0]?.textContent.trim() ?? ''
        : ''
    }

    this.updatePrice()
    this.updateSubmitButton()
  }

  /**
   * Writes the combined (main + post - discount) price into this drawer's price
   * element and publishes the post's contribution as a global addon so the
   * variant machinery keeps the price correct when the main variant changes.
   */
  updatePrice () {
    const addon = this.computeAddon()
    document.body.dataset.postPriceAddon = String(addon)

    const priceEl = this.priceElement
    if (priceEl) priceEl.innerHTML = formatMoney(this.getMainPrice() + addon)
  }

  /**
   * @returns {number}
   */
  computeAddon () {
    const checkedPost = this.querySelector(
      '[data-quick-add-post-el="post-radio"]:checked',
    )
    if (!checkedPost) return 0

    const discount = Number(checkedPost.dataset.postDiscount) || 0
    const sizeSelect = this.querySelector(
      '[data-quick-add-post-el="size-select"]',
    )
    const selectedOption = sizeSelect?.selectedOptions[0]

    const price =
      selectedOption && selectedOption.value
        ? Number(selectedOption.dataset.price) || 0
        : Number(checkedPost.dataset.postFromPrice) || 0

    return Math.max(0, price - discount)
  }

  /**
   * @returns {number}
   */
  getMainPrice () {
    const root = this.closest('.prd-QuickAdd_Inner')
    const radios = root?.querySelector('variant-radios')

    if (radios?.currentVariant) return radios.currentVariant.price

    return Number(this.dataset.basePrice) || 0
  }

  /**
   * @returns {HTMLElement|null}
   */
  get priceElement () {
    const root = this.closest('.prd-QuickAdd_Inner')
    return root ? root.querySelector('[data-product-price]') : null
  }

  updateSubmitButton () {
    const formId = this.dataset.formId
    if (!formId) return
    const button = document.querySelector(
      `#${formId} [data-product-add-to-cart]`,
    )
    if (!button) return
    const hasSize = !!this.variantInput?.value
    button.disabled = !hasSize
    const textEl = button.querySelector('[data-product-add-to-cart-text]')
    if (
      textEl &&
      button.dataset.addToCartText &&
      button.dataset.postPendingText
    ) {
      textEl.textContent = hasSize
        ? button.dataset.addToCartText
        : button.dataset.postPendingText
    }
  }

  get variantInput () {
    const formId = this.dataset.formId
    if (!formId) return null
    return document.querySelector(
      `#${formId} [data-product-post-el="variant-id"]`,
    )
  }

  get productInput () {
    const formId = this.dataset.formId
    if (!formId) return null
    return document.querySelector(
      `#${formId} [data-product-post-el="product-id"]`,
    )
  }
}

if (!customElements.get('product-quick-add-post')) {
  customElements.define('product-quick-add-post', ProductQuickAddPost)
}
